import { HttpClient } from '@angular/common/http';

import { Injectable } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import * as cryptoJS from 'crypto-js';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Subject } from 'rxjs';

import { AppGlobals } from '@app/app.global';
import { TagService } from '@app/modules/tag/services';
import { environment } from '@env/environment';
import { UserRole } from '../interfaces';
import { BankingService } from '@app/modules/payments/services/banking.service';

@Injectable({
  providedIn: 'root'
})
export class SharedService {
  cancelAPICalls$ = new Subject<void>(); // Subject to cancel API calls being called while user logs out
  KYCStatus = new BehaviorSubject<any>('blank');
  currentKYCStatus = this.KYCStatus.asObservable();

  navBarPETab = new BehaviorSubject<string>(null);
  currentNavBarPETab = this.navBarPETab.asObservable();

  loggedInUserThumbnail = new BehaviorSubject<string>(null);
  currentLoggedInUserThumbnail = this.loggedInUserThumbnail.asObservable();

  sideBarActive = new BehaviorSubject<boolean>(false);
  currentSideBarActive$ = this.sideBarActive.asObservable();

  companyDropdownChangeForAdmin = new BehaviorSubject<any>(null);
  currentCompanyDropdownChangeForAdmin$ = this.companyDropdownChangeForAdmin.asObservable();

  companyListForAdmin = new BehaviorSubject<any[]>([]);
  currentCompanyListForAdmin$ = this.companyListForAdmin.asObservable();

  // Hyperpure integration status
  hyperpureEnabled = new BehaviorSubject<boolean>(false);
  currentHyperpureEnabled$ = this.hyperpureEnabled.asObservable();

  openSingleEntitySelect$ = new Subject<void>();
  openSingleSelectionCompanyDropdown$ = new Subject<void>();
  openMultiSelectEntityDropdown$ = new Subject<void>();

  resetSelectedEntityFormControl$ = new Subject<void>();
  syncReviewedEntity$ = new Subject<void>();

  exploreProductStats = new BehaviorSubject<any>({});

  public currentInvoiceDetail: { companyId: string, invoiceId: string } = { companyId: '', invoiceId: '' };

  uomFlagsReady$ = new BehaviorSubject<boolean>(false);


  constructor(private toastr: ToastrService, private tagService: TagService,
    private http: HttpClient, private appGlobals: AppGlobals,
    private readonly bankingService: BankingService,
  ) { }

  encrypt(message: string) {
    if (!message) return null;

    const key = cryptoJS.enc.Base64.parse(environment.storageEncryptionKey);
    const iv = cryptoJS.enc.Base64.parse(environment.storageEncryptionIv);

    return cryptoJS.AES.encrypt(message.trim(), key, {
      iv,
      mode: cryptoJS.mode.CBC,
      padding: cryptoJS.pad.Pkcs7
    }).toString();
  }

  decrypt(message: string) {
    try {
      if (!message) return null;

      const key = cryptoJS.enc.Base64.parse(environment.storageEncryptionKey);
      const iv = cryptoJS.enc.Base64.parse(environment.storageEncryptionIv);

      return cryptoJS.AES.decrypt(message.trim(), key, {
        iv,
        mode: cryptoJS.mode.CBC,
        padding: cryptoJS.pad.Pkcs7
      }).toString(cryptoJS.enc.Utf8);
    } catch (e) {
      return null;
    }
  }

  parseJsonValue(value: any): any {
    if (value == null || value === '') {
      return null;
    }
    if (typeof value === 'object') {
      return value;
    }
    try {
      return JSON.parse(String(value));
    } catch {
      return value;
    }
  }

  /** Encrypt a string and return a URL-safe Base64 string */
  encryptForUrl(message: string): string {
    const encrypted = this.encrypt(message);
    if (!encrypted) return '';
    // Make Base64 URL-safe: + → -, / → _, strip trailing =
    return encrypted.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  /** Decrypt a URL-safe Base64 string back to plain text */
  decryptFromUrl(urlSafeMessage: string): string {
    if (!urlSafeMessage) return '';
    // Restore standard Base64: - → +, _ → /
    let base64 = urlSafeMessage.replace(/-/g, '+').replace(/_/g, '/');
    // Re-add padding
    const pad = base64.length % 4;
    if (pad) base64 += '='.repeat(4 - pad);
    return this.decrypt(base64) || '';
  }

  successAlert(message?: string) {
    this.toastr.success(message, '', {
      closeButton: true,
      timeOut: 3000,
      toastClass: `pmd-toastr toast-success ngx-toastr`
    });
  }

  errorAlert(message?: string) {
    this.toastr.error(message, '', {
      closeButton: true,
      timeOut: 3000,
      toastClass: `pmd-toastr toast-Error ngx-toastr`
    });
  }

  warningAlert(message?: string) {
    this.toastr.warning(message, '', {
      closeButton: true,
      timeOut: 3000,
      toastClass: `pmd-toastr toast-warning ngx-toastr`
    });
  }

  isFieldDirty(field: AbstractControl, isSubmitAttempted: boolean = true) {
    return field.dirty || field.touched || isSubmitAttempted;
  }

  pmdStatusClass(field: AbstractControl, isSubmitAttempted: boolean = true) {
    return (field.dirty || field.touched || isSubmitAttempted) &&
      { 'pmd-textfield-is-invalid': field.errors };
  }

  statusClass(field: AbstractControl, isSubmitAttempted: boolean = true) {
    return (field.dirty || field.touched || isSubmitAttempted) &&
      { 'is-invalid': field.errors };
  }

  hasErrors(field: AbstractControl, isSubmitAttempted: boolean = true) {
    return (field.dirty || field.touched || isSubmitAttempted) && field.errors;
  }

  private successMessageSubject: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  successMessage$ = this.successMessageSubject.asObservable();
  setSuccessMessage(message: string) {
    this.successMessageSubject.next(message);
  }

  /**
    * allow number only
    * @param event 
    * @returns 
    */
  numberOnly(event) {
    if (event.type == 'keyup' && /\D/g.test(event.target.value)) {
      event.target.value = event.target.value.replace(/\D/g, '');
    } else if (event.type == 'paste') {
      let text = (event.originalEvent || event).clipboardData.getData('text/plain');
      let trimText = text.replace(/\D/g, '');
      event.clipboardData.setData('text/plain', (trimText));
      event.preventDefault();
      document.execCommand('insertHTML', false, trimText);
    } else if (event.type == 'keypress' && /\D/g.test(event.target.value) == false) {
      const charCode = (event.which) ? event.which : event.keyCode;

      if (charCode > 31 && (charCode < 48 || charCode > 57)) {
        return false;
      }

      return true;
    }
  }

  numberOnlyWithDot(event) {
    if (event.type == 'keyup' && /\D/g.test(event.target.value)) {
      event.target.value = event.target.value.replace(/\D/g, '');
    } else if (event.type == 'paste') {
      let text = (event.originalEvent || event).clipboardData.getData('text/plain');
      let trimText = text.replace(/\D/g, '');
      event.clipboardData.setData('text/plain', (trimText));
      event.preventDefault();
      document.execCommand('insertHTML', false, trimText);
    } else if (event.type == 'keypress' && /\D/g.test(event.target.value) == false) {
      const charCode = (event.which) ? event.which : event.keyCode;
      if (charCode == 46) {
        return true;
      }

      if (charCode > 31 && (charCode < 48 || charCode > 57)) {
        return false;
      }

      return true;
    }
  }


  /**
   * The formatNumber function is designed to format a given number, along with an optional currency symbol, and return the formatted value as a 
   * string with comma-separated thousands.
   * @author Darshan Malani - 2nd Aug 2023, 10:AM PM
   * @param
   */
  formatNumber(number: number, isCurrency = false, minimumFractionDigits = 0, maximumFractionDigits = 0, isStrict = false) {
    number = number || 0;

    if (isNaN(number)) {
      return '0';
    }

    const options = {
      minimumFractionDigits: (isStrict && number % 1) ? maximumFractionDigits : minimumFractionDigits,
      maximumFractionDigits
    };

    if (isCurrency) {
      options['style'] = 'currency';
      options['currency'] = 'INR';
    }

    const currencyFormatter = new Intl.NumberFormat('en-IN', options);

    return currencyFormatter.format(number);
  }

  roundDown(number) {
    if (!number) {
      return 0;
    }

    number = Math.floor(number);
    const tenthCount = Math.floor(Math.log10(Math.abs(number)));
    const lowestSameDigit = Math.pow(10, tenthCount);

    return Math.floor(number / lowestSameDigit) * lowestSameDigit;
  }

  /** 
  * So, when you call roundup(345678), it will return 360000. nearest integer greater than or equal to the result.
  * @author Darshan Malani - 2nd Aug 2023, 10:AM PM
  * @param 
  */
  roundUp(number) {
    if (!number) {
      return 0;
    }

    number = Math.floor(number);
    const tenthCount = Math.floor(Math.log10(Math.abs(number)));
    const lowestSameDigit = Math.pow(10, tenthCount);
    const lowestSameMinusOneDigit = Math.pow(10, tenthCount - 1);

    return Math.floor(number / lowestSameDigit) * lowestSameDigit + (Math.ceil(number % lowestSameDigit / lowestSameMinusOneDigit) + 1) * lowestSameMinusOneDigit;
  }

  getDateString = (date: Date) =>
    date.toLocaleDateString('en-US', { month: 'short' }) + ' ' +
    date.toLocaleDateString('en-US', { day: '2-digit' }) + ', ' +
    date.toLocaleDateString('en-US', { year: 'numeric' });

  getTimeIntervals = (startDate, endDate) => {
    // Ensure start is the earlier date;
    if (startDate > endDate) {
      const t = endDate;

      endDate = startDate;
      startDate = t;
    }

    // Copy input start date do don't affect original
    startDate = new Date(startDate);
    let startInterval;
    let endInterval;
    let intervals;

    startInterval = this.getDateString(startDate);
    endInterval = this.getDateString(endDate);
    intervals = [startInterval];

    // List quarters from start to end
    while (startInterval != endInterval) {
      startDate.setDate(startDate.getDate() + 1);
      startInterval = this.getDateString(startDate);
      intervals.push(startInterval);
    }

    return intervals;
  }

  /** 
  * Capitalizes the first letter of each word in the input string.
  * @param {string} string - The input string to be capitalized.
  * @returns {string} The capitalized string.
  * @author Darshan Malani - 10th Aug 2023, 02:18 PM
  */
  capitalize(string: string): string {
    return string && typeof string === 'string' ? string.toLowerCase().replace(/(^\w)|(\s+\w)/g, letter => letter.toUpperCase()) : '' + string;
  }

  getObjectPropertyCount(o) {
    let count = 0;

    for (const k in o) {
      if (o.hasOwnProperty(k)) {
        count++;
      }
    }

    return count;
  }

  isEqualObjects(v1, v2) {
    if (typeof (v1) !== typeof (v2)) {
      return false;
    }

    if (typeof (v1) === 'function') {
      return v1.toString() === v2.toString();
    }

    if (v1 instanceof Object && v2 instanceof Object) {
      if (this.getObjectPropertyCount(v1) !== this.getObjectPropertyCount(v2)) {
        return false;
      }

      for (const k in v1) {
        if (!this.isEqualObjects(v1[k], v2[k])) {
          return false;
        }
      }

      return true;
    } else {
      return v1 === v2;
    }
  }

  shortName(name: string) {
    return name.split(' ').reduce((combinedNamePart, namePart) => combinedNamePart + namePart[0], '')?.toUpperCase();
  }

  toNumber(string, isRoundOffRequired = true, roundOffDigit = 2) {
    string = '' + (string ?? 0);
    const roundOff = Math.pow(10, roundOffDigit);
    let number = string.replace(/\s/g, '')
      .replace(/rs\.?/ig, '')
      .replace(/\.+$/, '')
      .replace(/\(([^)]*[^\d.,)][^)]*)\)/g, '')
      .replace(/(?<=\d+)[^\d.,](?=\d+$)/, '.')
      .replace(/[^\d.]/ig, '')
      .replace(/\.\.+/g, '.')
      .replace(/(?<=\d*)\.(?=\d*\.\d+)/g, '');
    number = string[0] === '-' ? -number : +number;
    return number ? isRoundOffRequired ? Math.round(number * roundOff) / roundOff : number : 0;
  };

  removeEmptyFields(object): any {
    for (let key in object) {
      if (object[key] === null || object[key] === undefined || object[key] === '') {
        delete object[key];
      }
    }

    return object;
  }

  copyToClipboard(text: string) {
    const el = document.createElement('textarea');
    el.value = text;
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
  }

  escapeRegExp(string: string): string {
    return string.replace(/[-/.*+:?^${}()|[\]\\]/g, '\\$&');
  }

  focusOut(IdSelected) {
    document.getElementById('product-name-' + IdSelected).style.opacity = '0';
    document.querySelectorAll('.khr-select-in-table .ng-value').forEach(element => {
      element.classList.add('showing-lable');
    });
  }

  focusIn(IdSelected) {
    document.getElementById('product-name-' + IdSelected).style.opacity = '1';

    const ngSelectElement = document.getElementById('product-item-' + IdSelected);
    if (ngSelectElement) {
      const ngValueElement = ngSelectElement.querySelector('.showing-lable');
      if (ngValueElement) {
        ngValueElement.classList.remove('showing-lable');
      }
    }
  }

  suggestedProductsUniq(productSItems = []) {
    const exist = new Set();
    const uniqueProductSItems = [];

    const filterProducts = productSItems
      .map(product => product?.trim().replace(/\s+/g, ' ').toLowerCase())
      .filter(product => product);

    filterProducts.forEach(Products => {
      const lowerCaseItem = Products.toLowerCase();
      if (!exist.has(lowerCaseItem)) {
        exist.add(lowerCaseItem);
        uniqueProductSItems.push(Products);
      }
    });

    return uniqueProductSItems;
  }

  humanizeSeconds(s: number) {
    if (s < 0) {
      return '0:00';
    }

    const years = Math.floor(s / 31536000);
    const days = Math.floor((s % 31536000) / 86400);
    const hours = Math.floor((s % 86400) / 3600);
    const minutes = Math.floor((s % 3600) / 60);
    const seconds = s % 60

    return (years ? years + ' years ' : '') +
      (days ? days + ' days ' : '') +
      (hours ? `${hours}`.padStart(2, '0') + ':' : '') +
      `${minutes}`.padStart(2, '0') + ':' +
      `${seconds}`.padStart(2, '0');
  }

  isRequired(formGroup: FormGroup, field: string) {
    const form_field = formGroup.get(field);
    if (!form_field.validator) {
      return false;
    }

    const validator = form_field.validator({} as AbstractControl);
    return (validator && validator.required);
  }

  maskMobile(number) {
    return number?.replace(/\d(?=\d{4})/g, "*");
  }

  maskEmail(email) {
    if (email) {
      const [name, domain] = email.split("@");
      const maskedName = name.substring(0, 2) + "*".repeat(name.length - 2);
      return maskedName + "@" + domain;
    }
  }

  updateKYCStatus(status) {
    this.KYCStatus.next(status);
  }

  hasNumber(value: string): boolean {
    return /\d/.test(value);
  }

  hasSpecialCharacter(value: string): boolean {
    return /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(value);
  }

  updateNavBarPETab(tab: string) {
    this.navBarPETab.next(tab);
    localStorage.setItem('navBarPETab', tab);
  }

  updateLoggedInUserThumbnail(thumbnail: string) {
    this.loggedInUserThumbnail.next(thumbnail);
  }

  updateCurrentSideBarStatus(flag) {
    this.sideBarActive.next(flag);
  }

  fetchSupplierTags(param = {}) {
    this.tagService.getSupplierTags(param).subscribe(async (res) => {
      if (res) {
        this.setSupplierTags(res);
      }
    });
  }

  setSupplierTags(supplierWiseRateExclusive) {
    localStorage.setItem("supplierWiseRateExclusive", JSON.stringify(supplierWiseRateExclusive));
  }

  getSupplierTags() {
    return JSON.parse(localStorage.getItem("supplierWiseRateExclusive"));
  }

  fetchSuggestedGSTAndPan(param = {}) {
    this.getCompanyGstPan(param).subscribe(async (res) => this.setSuggestedGSTAndPan(res));
  }

  getCompanyGstPan(company = {}) {
    return this.http.get<any>(`${this.appGlobals.apiUrl.invoice}company-gst-pan`, {
      params: company
    });
  }

  setSuggestedGSTAndPan(suggested) {
    localStorage.setItem("suggestedGSTAndPan", JSON.stringify(suggested.suggestedGSTAndPan));
    localStorage.setItem("suggestedProducts", suggested.suggestedProducts);
  }

  setReviewedGSTAndPan(suggestedGSTAndPan) {
    localStorage.setItem("suggestedGSTAndPan", JSON.stringify(suggestedGSTAndPan));
  }

  getSuggestedGSTAndPan() {
    return JSON.parse(localStorage.getItem("suggestedGSTAndPan"));
  }

  getSuggestedProducts(supplierName) {
    if (!supplierName) { return []; }

    const suggestedProducts = JSON.parse(localStorage.getItem("suggestedProducts"));
    const escapeRegex = str => str.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
    const regex = new RegExp(`^${escapeRegex(supplierName)}$`, 'i'); // case-insensitive match
    const matchedKey = Object.keys(suggestedProducts).find(key => regex.test(key));

    const result = matchedKey ? suggestedProducts[matchedKey] : [];
    return result;
  }

  updateSuggestedProducts(supplierName, products) {
    const suggestedProducts = JSON.parse(localStorage.getItem("suggestedProducts"));

    const escapeRegex = str => str.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&'); // KHAR-3021
    const regex = new RegExp(`^${escapeRegex(supplierName)}$`, 'i'); // case-insensitive match
    const matchedKey = Object.keys(suggestedProducts).find(key => regex.test(key));

    if (matchedKey) {
      const existingItems = suggestedProducts[matchedKey];
      const updatedItems = Array.from(new Set([...existingItems, ...products.map(item => item.description)]));
      suggestedProducts[matchedKey] = updatedItems;
    } else {
      const updatedItems = Array.from(new Set([...products.map(item => item.description)]));
      suggestedProducts[supplierName] = updatedItems;

    }

    localStorage.setItem("suggestedProducts", JSON.stringify(suggestedProducts));
  }

  hasNumberOrLetter(value) {
    return /.*[A-Za-z0-9].*/.test(value);
  }

  isZeroOrPositiveNumber(value) {
    return /^\s*(0|[1-9]\d*(\.\d+)?|0?\.\d+)\s*$/.test(value);
  }

  isOnlyNumbers(value) {
    return /^\d+$/.test(value);
  }

  saveInvoiceToLocalStorage(invoice: any, companyId: string) {
    const key = `invoice_drafts_${companyId}`;
    const existing = JSON.parse(localStorage.getItem(key) || '[]');

    // Update if invoice already exists
    const index = existing.findIndex((inv: any) => inv.id === invoice.id);
    if (index > -1) {
      existing[index] = invoice;
    } else {
      existing.push(invoice);
    }

    localStorage.setItem(key, JSON.stringify(existing));
  }

  getInvoiceFromLocal(companyId: string, invoiceId: string): any | null {
    const key = `invoice_drafts_${companyId}`;
    const drafts = JSON.parse(localStorage.getItem(key) || '[]');
    return drafts.find((inv: any) => inv.id === invoiceId) || null;
  }

  removeInvoiceDraft(companyId: string, invoiceId: string) {
    const key = `invoice_drafts_${companyId}`;
    const drafts = JSON.parse(localStorage.getItem(key) || '[]');
    const updated = drafts.filter((inv: any) => inv.id !== invoiceId);
    localStorage.setItem(key, JSON.stringify(updated));
  }

  displayOtherReason(statuses, field) {
    if (!statuses?.length) {
      return;
    }

    const adminStatus = statuses.find(i => i.role === UserRole.Admin && i.invoiceStatus === 'Draft' && i.otherReason);
    const ownerStatus = statuses.find(i => i.role === UserRole.Owner && i.invoiceStatus === 'Draft' && i.otherReason);
    const outletStatus = statuses.find(i => i.role === UserRole.Outlet && i.invoiceStatus === 'Draft' && i.otherReason);

    if (adminStatus) {
      return field === 'message' ? adminStatus.otherReason : adminStatus.updatedBy;
    } else if (ownerStatus) {
      return field === 'message' ? ownerStatus.otherReason : ownerStatus.updatedBy;
    } else if (outletStatus) {
      return field === 'message' ? outletStatus.otherReason : outletStatus.updatedBy;
    }
  };

  /**
   * This method will set the current invoice details in shared service.
   * @param companyId 
   * @param invoiceId 
   */
  setCurrentInvoice(companyId: string, invoiceId: string) {
    this.currentInvoiceDetail.companyId = companyId;
    this.currentInvoiceDetail.invoiceId = invoiceId;
  }

  triggerSingleSelectDropdown() {
    this.openSingleEntitySelect$.next();
  }

  triggerSingleSelectionCompanyDropdown() {
    this.openSingleSelectionCompanyDropdown$.next();
  }

  triggerMultiSelectEntityDropdown() {
    this.openMultiSelectEntityDropdown$.next();
  }

  toggleBlurBackground(canBlur = false) {
    const mainBlockDivElement = document.getElementsByClassName('main-block')[0];
    if (mainBlockDivElement) {
      if (canBlur) {
        mainBlockDivElement.classList.add('layout-blur');
      } else {
        mainBlockDivElement.classList.remove('layout-blur');
      }
    }
  }

  resetSelectedEntityFormControl() {
    this.resetSelectedEntityFormControl$.next();
  }

  setExploreProductStats(stats) {
    this.exploreProductStats.next(stats);
  }

  getExploreProductsStats() {
    return this.exploreProductStats.value;
  }

  checkFileNameSanity(fileName) {
    // Reject null, undefined, or non-string types
    if (typeof fileName !== 'string' ||
      !(fileName = fileName.trim()) || // trims and checks for empty string
      /(\.\.|[\\/]|<script|javascript:|%00)/i.test(fileName) || // path/script/null byte
      !/^[a-zA-Z0-9._\-\s()]+$/.test(fileName) // character whitelist (allows space & brackets)
    ) {
      return false;
    }
    const lower = fileName.toLowerCase();
    return !['.js', '.exe', '.php', '.sh', '.bat', '.cmd'].some(ext => lower.endsWith(ext));
  };

  getKYC() {
    this.bankingService.getKYC().subscribe((response: any) => {
      if (response && Object.keys(response).length) {
        this.updateKYCStatus(response.kycStatus);
      } else {
        this.updateKYCStatus(null);
      }
    });
  }
  /**
   * 
   * @param companies 
   * This method will set standardGSTUOM flag for each company
   */
  setStandardGSTUOMForCompanies(companies) {
    const standardGSTUOMForCompanies = [];

    for (const company of companies) {
      if (!company?.parent) {
        const companyExistIndex = standardGSTUOMForCompanies?.findIndex(findCompany => findCompany.id == company.parent);
        if (companyExistIndex == -1) {
          standardGSTUOMForCompanies.push({
            id: company.id,
            standardGSTUOM: company.standardGSTUOM == undefined ? false : company.standardGSTUOM == false ? false : true,
            inventoryRawMaterialsForReviewPage: company.inventoryRawMaterialsForReviewPage == undefined ? false : company.inventoryRawMaterialsForReviewPage == false ? false : true
          });
        }
      } else {
        const companyExistIndex = standardGSTUOMForCompanies?.findIndex(findCompany => findCompany.id == company.parent);
        // find Parent
        if (companyExistIndex == -1) {
          const parentCompany = companies.find((findCompany) => findCompany.id == company.parent);
          if (parentCompany) {
            standardGSTUOMForCompanies.push({
              id: company.parent,
              standardGSTUOM: parentCompany.standardGSTUOM == undefined ? false : parentCompany.standardGSTUOM == false ? false : true,
              inventoryRawMaterialsForReviewPage: parentCompany.inventoryRawMaterialsForReviewPage == undefined ? false : parentCompany.inventoryRawMaterialsForReviewPage == false ? false : true
            });
          } else {
            standardGSTUOMForCompanies.push({
              id: company.parent,
              standardGSTUOM: company.standardGSTUOM == undefined ? false : company.standardGSTUOM == false ? false : true,
              inventoryRawMaterialsForReviewPage: company.inventoryRawMaterialsForReviewPage == undefined ? false : company.inventoryRawMaterialsForReviewPage == false ? false : true
            });
          }
        }
      }
    }
    localStorage.setItem("standardGSTUOMForCompanies", JSON.stringify(standardGSTUOMForCompanies));
    this.uomFlagsReady$.next(true);
  }

  /**
   * 
   * @returns standardGSTUOM from local storage
   */
  getStandardGSTUOMForCompanies() {
    return JSON.parse(localStorage.getItem('standardGSTUOMForCompanies'));
  }

  setAIExtractionsRequests(aiExtractionRequests) {
    localStorage.setItem('ai-extraction-requests', JSON.stringify(aiExtractionRequests));
  }

  getAIExtractionRequests() {
    return JSON.parse(localStorage.getItem('ai-extraction-requests'));
  }

  onCompanyChangeForAdmin(selectedCompany: any) {
    this.companyDropdownChangeForAdmin.next(selectedCompany);
  }

  setAdminCompanyList(companies: any) {
    this.companyListForAdmin.next(companies);
  }
}